"use client"

import * as React from "react"

import { useTranslations } from "@/i18n"
import {
  DataTableMobileLabelsProvider,
  MobilePickerLabelsProvider,
  type DataTableMobileLabels,
  type MobilePickerLabels,
} from "@ibada/ui"

/** Gives every DataTable's phone list ("Charger plus", selection, sort) its strings in the app's language. */
export function TenantDataTableMobileLabels({ children }: { children: React.ReactNode }) {
  const t = useTranslations("common.dataTableMobile")
  const labels = React.useMemo<Partial<DataTableMobileLabels>>(
    () => ({
      loadMore: t("loadMore"),
      shownOfTotal: (shown, total) => t("shownOfTotal", { shown, total }),
      allLoaded: t("allLoaded"),
      loading: t("loading"),
      sortBy: (label) => t("sortBy", { label }),
      sort: t("sort"),
      cancelSelection: t("cancelSelection"),
      select: t("select"),
      selectDescription: t("selectDescription"),
      selectAll: t("selectAll"),
      selectedCount: (count) => (count === 1 ? t("selectedOne", { count }) : t("selectedOther", { count })),
      moreActions: t("moreActions"),
      clearSearch: t("clearSearch"),
      longPressHint: t("longPressHint"),
      filtersTitle: t("filtersTitle"),
      filtersApply: t("filtersApply"),
      filtersReset: t("filtersReset"),
      filtersChoose: t("filtersChoose"),
      filtersAny: t("filtersAny"),
      filtersFrom: t("filtersFrom"),
      filtersTo: t("filtersTo"),
      filtersMin: t("filtersMin"),
      filtersMax: t("filtersMax"),
      filtersSearch: t("filtersSearch"),
      filtersNoResults: t("filtersNoResults"),
      filtersClear: t("filtersClear"),
      filtersSelectedCount: (count) =>
        count === 1 ? t("filtersSelectedOne", { count }) : t("filtersSelectedOther", { count }),
    }),
    [t],
  )
  return <DataTableMobileLabelsProvider labels={labels}>{children}</DataTableMobileLabelsProvider>
}

/** Gives every phone picker sheet (selects, menus, form pickers) its strings in the app's language. */
export function TenantMobilePickerLabels({ children }: { children: React.ReactNode }) {
  const t = useTranslations("common.mobilePicker")
  const labels = React.useMemo<Partial<MobilePickerLabels>>(
    () => ({
      done: t("done"),
      search: t("search"),
      clearSearch: t("clearSearch"),
      noResults: t("noResults"),
      create: (query) => t("create", { query }),
      clear: t("clear"),
    }),
    [t],
  )
  return <MobilePickerLabelsProvider labels={labels}>{children}</MobilePickerLabelsProvider>
}
